import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { parsePeekHref } from "@/components/peek/parse-peek-href";
import { isViewPath } from "@/components/views/views-data";
import type { OrgContext } from "@/hooks/use-org-context";
import type { SourceRecordRef } from "@/lib/api/entities";
import type { BrowserHandoff } from "@/lib/api/notifications";
import {
	destinationHrefFromPeekSearch,
	hasPeekFlag,
	implicitPeekEntityPath,
	mergePeekSearch,
	omitPeekSearch,
	peekSearchFromDestinationHref,
	pickPeekSearch,
} from "@/lib/peek-search";
import type { SourceAttribution } from "@/lib/source-attribution";

/**
 * A clicked activity/notification item that should open in the right-side peek
 * pane (instead of navigating to its dedicated page). The pane reuses the SAME
 * content component the dedicated page renders — never a duplicate.
 *
 * Peek target is sticky in the URL (`?peek=1&peek_run_id=…` etc.) so reloads,
 * sidepanel deep-links, and "click → query param" all share one path.
 */
export interface PeekItem {
	/** Local connector controls reuse the workspace peek, with no navigation or new API. */
	connectorPanel?: {
		connectorKey: string;
		organizationId: string;
		ownerSlug: string;
	} & (
		| { section: "feeds" | "actions"; connectionId?: number }
		| { section: "connections"; connectionId?: never }
		/** The connector's OAuth apps; `newApp` opens the add form. */
		| { section: "oauth-apps"; connectionId?: never; newApp?: boolean }
	);
	/** App-relative href of the destination (same string a plain click navigates to). */
	href: string;
	/**
	 * Back-stack identity for a local item, whose `href` is empty: two fields
	 * of one record are different places to go back to.
	 */
	key?: string;
	/**
	 * A field the record page owns, read from that page's draft (and edited
	 * there while the page is in edit mode): the field as a list, or one item
	 * of it (`row`). Local only — a row index is meaningful for this draft,
	 * not as a reloadable link.
	 */
	recordField?: { entityId: number | string; field: string; row?: number };
	/** Every current link of one relationship type from a record, paged. */
	recordLinks?: {
		entityId: number;
		entityName: string;
		ownerSlug: string;
		orgContext: OrgContext;
		relationshipType?: string;
		label: string;
	};
	/** Another record's fields, read-only (a linked record). */
	recordPeer?: {
		ownerSlug: string;
		orgContext: OrgContext;
	} & (
		| { entityId: number; record?: never }
		| { record: SourceRecordRef; entityId?: never }
	);
	/** Title for the pane header. */
	title: string;
	/** The specific run this item represents (shows just that run, not the whole automation). */
	runId?: number;
	/** Snapshot used when the notification has no focused app destination. */
	body?: string | null;
	notificationType?: string;
	createdAt?: string;
	/**
	 * Browser-handoff target (e.g. a tweet the user should open to act on a
	 * staged draft). Present only on browser-handoff notifications; when set,
	 * the pane renders the notification details with an "Open" action instead
	 * of an app destination.
	 */
	browserUrl?: string | null;
	/** Live lifecycle of the page-activated draft behind browserUrl. */
	browserHandoff?: BrowserHandoff | null;
	/** Workspace and durable producer/source metadata shown in Why/how. */
	owner?: string;
	sourceAttribution?: SourceAttribution | null;
	/**
	 * Local snapshot of a connector's schema — a feed (config + event kinds) or
	 * an action (input + output). These have no dedicated app page, so the pane
	 * renders from this payload instead of a destination href.
	 */
	schemaSections?: {
		description?: string;
		sections: Array<{
			title: string;
			description?: string;
			schema?: Record<string, unknown>;
		}>;
	};
}

interface NotificationPeekValue {
	item: PeekItem | null;
	open: (item: PeekItem) => void;
	close: () => Promise<void>;
	/**
	 * Return to the peek this one was opened from, if any. Drilling used to be
	 * one-way: every peek opened with `replace: true`, which was fine while the
	 * list you drilled from stayed on the page beside the pane. A record's
	 * activity list lives INSIDE the pane, so opening an event from it replaced
	 * the only copy of the list and left nothing to go back to.
	 */
	back: (() => void) | null;
	/**
	 * Point the open peek at another tab of the same subject (an event's Event
	 * tab or one of its views) without drilling: the back stack is unchanged,
	 * and the URL keys follow like any other peek.
	 */
	retarget: (href: string) => void;
}

const NotificationPeekContext = createContext<NotificationPeekValue | null>(
	null,
);

function titleForPeekHref(href: string, fallback?: string): string {
	if (fallback?.trim()) return fallback.trim();
	const dest = parsePeekHref(href);
	if (!dest) return "Details";
	if (dest.kind === "memory" && "runId" in dest) {
		return `Run #${dest.runId}`;
	}
	if (dest.kind === "runs") return "Runs history";
	if (
		dest.kind === "memory" ||
		dest.kind === "entity" ||
		dest.kind === "entity-type"
	)
		return "Activity";
	if (dest.kind === "event") return "Event";
	if (dest.kind === "record-view") return "View";
	if (dest.kind === "automation") {
		return dest.runId != null ? `Automation run #${dest.runId}` : "Automation";
	}
	return "Details";
}

/**
 * Provides peek state for everything inside the owner (workspace) layout — i.e.
 * the surfaces that render the activity timeline (agent chat empty-state, Home).
 * When no provider is present (e.g. outside workspace routes), `useNotificationPeek`
 * returns null so callers fall back to a normal navigate().
 *
 * URL contract: see `lib/peek-search.ts`. Opening the pane writes peek keys
 * onto the current route's search; closing omits them. Incoming search (deep
 * link / click) hydrates the pane. On a view path (`/-/views/<key>`) the query
 * is the view's params, so the pane is local state there and never reads or
 * writes the query.
 */
export function NotificationPeekProvider({
	children,
}: {
	children: ReactNode;
}) {
	const navigate = useNavigate();
	const location = useLocation();
	const params = useParams({ strict: false }) as { owner?: string };
	const owner = params.owner ?? "";
	const onViewPath = isViewPath(location.pathname);

	const [item, setItem] = useState<PeekItem | null>(null);
	// Peeks opened from inside another peek, oldest first. In-memory rather than
	// history-based: `open` replaces the current entry, so the browser's own
	// back would leave the page rather than the pane.
	const [stack, setStack] = useState<PeekItem[]>([]);
	// URL → pane. Whenever the URL resolves to no peek the pane is closed, so
	// the drill stack goes with it — `close()` is not the only way out.
	useEffect(() => {
		// Keeps the empty array's identity so a peekless navigation does not
		// re-render every consumer of the context.
		const closePane = () => {
			setItem(null);
			setStack((prev) => (prev.length > 0 ? [] : prev));
		};
		if (!owner) {
			closePane();
			return;
		}
		if (onViewPath) return;
		const search = location.search as Record<string, unknown>;
		if (!hasPeekFlag(search)) {
			closePane();
			return;
		}
		const peekSearch = pickPeekSearch(search);
		// A bare `?peek=1` on a record's own page means that record — the path
		// already named it, so the URL does not repeat it.
		if (
			!peekSearch.peek_entity &&
			!peekSearch.peek_entity_type &&
			!peekSearch.peek_run_id &&
			!peekSearch.peek_content_ids &&
			!peekSearch.peek_automation &&
			!peekSearch.peek_runs
		) {
			const implicit = implicitPeekEntityPath(location.pathname);
			if (implicit) peekSearch.peek_entity = implicit;
		}
		const href = destinationHrefFromPeekSearch(owner, peekSearch);
		const dest = href ? parsePeekHref(href) : null;
		if (!href || !dest) {
			closePane();
			return;
		}
		const runId =
			dest.kind === "memory" && "runId" in dest
				? dest.runId
				: dest.kind === "automation"
					? dest.runId
					: undefined;
		setItem((prev) => {
			if (prev?.href === href && prev.runId === runId) return prev;
			// The opened href and the one the URL rebuilds can differ in form
			// (`/events?content_ids=5` opens, `/events/5` comes back): the same
			// target keeps the title its opener gave it.
			const prevTarget = prev ? peekSearchFromDestinationHref(prev.href) : null;
			const sameTarget =
				prev?.href === href ||
				(prevTarget !== null &&
					destinationHrefFromPeekSearch(owner, prevTarget) === href);
			return {
				href,
				title: titleForPeekHref(href, sameTarget ? prev?.title : undefined),
				runId,
			};
		});
	}, [location.search, location.pathname, owner, onViewPath]);

	const open = useCallback(
		(next: PeekItem) => {
			// `/events?content_ids=7` and `/events/7` are the same event: reopening
			// it must not push a Back entry that returns to itself.
			const identity = (peek: PeekItem) => {
				if (peek.key) return peek.key;
				const search = peekSearchFromDestinationHref(peek.href);
				return (
					(search && destinationHrefFromPeekSearch(owner, search)) || peek.href
				);
			};
			setStack((prev) =>
				item && identity(item) !== identity(next) ? [...prev, item] : prev,
			);
			const peekSearch = peekSearchFromDestinationHref(next.href);
			if (!peekSearch || onViewPath) {
				// Generic notifications have no exact-read API, so keep their
				// supplied snapshot local rather than writing an unreloadable URL.
				setItem(next);
				return;
			}
			// Browser-handoff notifications (browserUrl set) are also snapshot
			// content: the pane renders the notification + an external Open
			// action, not a reloadable app destination. Persisting the href
			// (e.g. peek_content_ids) would rebuild the item from the URL on the
			// navigate below and lose browserUrl, routing the pane to the wrong
			// content. Keep it local, like generic notifications.
			if (typeof next.browserUrl === "string" && next.browserUrl.length > 0) {
				setItem(next);
				return;
			}
			if (
				next.runId != null &&
				peekSearch.peek_automation &&
				!peekSearch.peek_run_id
			) {
				peekSearch.peek_run_id = String(next.runId);
			}
			// Opening a record's own peek from its own page: the path already says
			// which record, so leave the key out rather than echoing it.
			if (
				peekSearch.peek_entity &&
				peekSearch.peek_entity === implicitPeekEntityPath(location.pathname)
			) {
				peekSearch.peek_entity = undefined;
			}
			setItem(next);
			void navigate({
				// Stay on the current path; only stick peek keys into search.
				search: ((prev: Record<string, unknown>) =>
					mergePeekSearch(prev, peekSearch)) as never,
				replace: true,
			});
		},
		[navigate, location.pathname, item, onViewPath, owner],
	);

	const back = useCallback(() => {
		const previous = stack[stack.length - 1];
		if (!previous) return;
		setStack((prev) => prev.slice(0, -1));
		setItem(previous);
		const peekSearch = peekSearchFromDestinationHref(previous.href);
		// Snapshots have no reloadable URL. Restore them locally, just as open()
		// does, so URL hydration cannot close the pane or discard handoff data.
		if (!peekSearch || previous.browserUrl || onViewPath) return;
		if (
			peekSearch?.peek_entity &&
			peekSearch.peek_entity === implicitPeekEntityPath(location.pathname)
		) {
			peekSearch.peek_entity = undefined;
		}
		void navigate({
			search: ((prev: Record<string, unknown>) =>
				peekSearch
					? mergePeekSearch(prev, peekSearch)
					: omitPeekSearch(prev)) as never,
			replace: true,
		});
	}, [navigate, stack, location.pathname, onViewPath]);

	const retarget = useCallback(
		(href: string) => {
			setItem((prev) => (prev ? { ...prev, href } : prev));
			const peekSearch = peekSearchFromDestinationHref(href);
			if (!peekSearch || onViewPath) return;
			void navigate({
				search: ((prev: Record<string, unknown>) =>
					mergePeekSearch(prev, peekSearch)) as never,
				replace: true,
			});
		},
		[navigate, onViewPath],
	);

	const close = useCallback(async () => {
		setStack([]);
		setItem(null);
		if (onViewPath) return;
		await navigate({
			search: ((prev: Record<string, unknown>) =>
				omitPeekSearch(prev)) as never,
			replace: true,
		});
	}, [navigate, onViewPath]);

	const value = useMemo(
		() => ({
			item,
			open,
			close,
			back: stack.length > 0 ? back : null,
			retarget,
		}),
		[item, open, close, back, stack.length, retarget],
	);
	return (
		<NotificationPeekContext.Provider value={value}>
			{children}
		</NotificationPeekContext.Provider>
	);
}

/** Returns the peek controller when inside a provider, else null (caller navigates). */
export function useNotificationPeek(): NotificationPeekValue | null {
	return useContext(NotificationPeekContext);
}
